// src/components/Nav/NavComponent.tsx
import React, { useState, useRef, useCallback } from "react";
import "../../scss/nav.scss";
import { Button } from "../../common/components/Button";
import { Burger } from "../../common/components/BurgerIcon";
import { TypewriterText } from "../../common/components/TypewriterText";
import logoPL from "../../assets/CC_logo_XS_kolor_biale.png";
import logoENG from "../../assets/cc ang.png";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../../i18n/I18nProvider";
import type { TranslationKey } from "../../i18n/translations";

type NavSubLink = {
    id: string;
    labelKey: TranslationKey;
    to: string;
    description: string;
};

type NavItem = {
    id: string;
    labelKey: TranslationKey;
    to?: string;
    links?: NavSubLink[];
};

const NAV_ITEMS: NavItem[] = [
    {
        id: "about",
        labelKey: "nav_about",
        links: [
            {
                id: "about-team",
                labelKey: "nav_about_team",
                to: "/o_centrum/o_nas",
                description: "",
            },
            {
                id: "news",
                labelKey: "nav_news",
                to: "/aktualnosci",
                description: "",
            },
            {
                id: "contact",
                labelKey: "nav_contact",
                to: "/kontakt",
                description: "",
            },
            {
                id: "tactical-panel",
                labelKey: "nav_tactical_panel",
                to: "/panel-taktyczny",
                description: "",
            },
        ],
    },
    // {
    //     id: "projects",
    //     labelKey: "nav_projects",
    //     links: [
    //         {
    //             id: "projects-current",
    //             labelKey: "nav_projects_current",
    //             to: "/projekty/aktualne",
    //             description: "",
    //         },
    //         // {
    //         //     id: "projects-archive",
    //         //     labelKey: "nav_projects_archive",
    //         //     to: "/projekty/archiwum",
    //         //     description: "",
    //         // },
    //     ],
    // },
    {
        id: "students",
        labelKey: "nav_students",
        links: [
            {
                id: "calendar",
                labelKey: "nav_calendar",
                to: "/student/kalendarz",
                description: "",
            },
            {
                id: "clubs",
                labelKey: "nav_clubs",
                to: "/kola-naukowe",
                description: "",
            },
            {
                id: "proposals",
                labelKey: "nav_proposals",
                to: "/student/propozycje",
                description: "",
            },
            { id: "internships", labelKey: "nav_internships", to: "/praktyki", description: "" },
        ],
    },
    {
        id: "projects-current",
        labelKey: "nav_projects_current",
        to: "/projekty/aktualne",
    },
    { id: "raports", labelKey: "nav_raports", to: "/raporty" },
    { id: "business", labelKey: "nav_business", to: "/biznes" },
    { id: "environment", labelKey: "nav_environment", to: "/otoczenie" },
];

const NavComponent: React.FC = () => {
    const navigate = useNavigate();
    const { t, language, setLanguage, languages } = useI18n();
    const logo = language === "pl" ? logoPL : logoENG;

    const [isBurgerOpen, setIsBurgerOpen] = useState(false);
    const [openMobileId, setOpenMobileId] = useState<string | null>(null);
    const [isLangOpen, setIsLangOpen] = useState(false);

    const [openPanelId, setOpenPanelId] = useState<string | null>(null);
    const [hoveredPanelId, setHoveredPanelId] = useState<string | null>(null);

    // Track desktop typewriter completions → signal globe it's safe to load
    const desktopTwTotal = NAV_ITEMS.length;
    const desktopTwDone = useRef(0);
    const twEventFired = useRef(false);
    const handleDesktopTwComplete = useCallback(() => {
        desktopTwDone.current += 1;
        if (!twEventFired.current && desktopTwDone.current >= desktopTwTotal) {
            twEventFired.current = true;
            (
                window as Window & { __cyberNavTypewritersDone?: boolean }
            ).__cyberNavTypewritersDone = true;
            window.dispatchEvent(new CustomEvent("nav-typewriters-done"));
        }
    }, [desktopTwTotal]);

    const currentLang =
        languages.find((l) => l.code === language) || languages[0];

    const handleBurgerClick = () => {
        setIsBurgerOpen((prev) => !prev);
        setOpenMobileId(null);
        setIsLangOpen(false);
    };

    const handleMobileParentClick = (item: NavItem, hasPanel: boolean) => {
        if (!hasPanel) {
            if (item.to) navigate(item.to);
            setIsBurgerOpen(false);
            return;
        }
        setOpenMobileId((prev) => (prev === item.id ? null : item.id));
    };

    const handleMobileLinkClick = () => {
        setIsBurgerOpen(false);
    };

    return (
        <nav className="nav" aria-label={t("nav_main_label")}>
            <div className="nav__inner">
                <div className="nav__logo">
                    <button
                        type="button"
                        onClick={() => navigate("/")}
                        aria-label={t("nav_logo_alt")}
                    >
                        <img src={logo} alt={t("nav_logo_alt")} />
                    </button>
                </div>

                {/* DESKTOP */}
                <div className="nav__desktop">
                    {NAV_ITEMS.map((item) => {
                        const hasPanel = !!item.links?.length;
                        const panelId = `panel-${item.id}`;
                        const isOpenDesktop = openPanelId === item.id;

                        return (
                            <div
                                key={item.id}
                                className={`nav__item nav-panel ${
                                    hasPanel ? "nav-panel--has-panel" : ""
                                } ${isOpenDesktop ? "nav-panel--open" : ""}`}
                                onMouseEnter={() =>
                                    hasPanel && setHoveredPanelId(item.id)
                                }
                                onMouseLeave={() =>
                                    hasPanel &&
                                    setHoveredPanelId((prev) =>
                                        prev === item.id ? null : prev
                                    )
                                }
                                onBlur={(e) => {
                                    if (
                                        hasPanel &&
                                        !e.currentTarget.contains(
                                            e.relatedTarget as Node
                                        )
                                    ) {
                                        setOpenPanelId((prev) =>
                                            prev === item.id ? null : prev
                                        );
                                        setHoveredPanelId((prev) =>
                                            prev === item.id ? null : prev
                                        );
                                    }
                                }}
                                onKeyDown={(e) => {
                                    if (hasPanel && e.key === "Escape") {
                                        setOpenPanelId(null);
                                        setHoveredPanelId(null);
                                        const trigger =
                                            e.currentTarget.querySelector<HTMLButtonElement>(
                                                ".nav__itemButton"
                                            );
                                        trigger?.focus();
                                    }
                                }}
                            >
                                <Button
                                    type="button"
                                    variant="primary"
                                    className="nav__itemButton"
                                    aria-expanded={
                                        hasPanel ? isOpenDesktop : undefined
                                    }
                                    aria-controls={
                                        hasPanel ? panelId : undefined
                                    }
                                    onClick={() => {
                                        if (!hasPanel && item.to) {
                                            navigate(item.to);
                                        }
                                    }}
                                    onKeyDown={(e) => {
                                        if (!hasPanel) return;
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            setOpenPanelId((prev) =>
                                                prev === item.id
                                                    ? null
                                                    : item.id
                                            );
                                        }
                                    }}
                                >
                                    <TypewriterText
                                        text={t(item.labelKey)}
                                        active={true}
                                        onComplete={handleDesktopTwComplete}
                                    />
                                </Button>

                                {hasPanel && (
                                    <div
                                        id={panelId}
                                        className="nav__dropdown panel"
                                    >
                                        {item.links!.map((link) => (
                                            <Link
                                                key={link.id}
                                                to={link.to}
                                                className="panel__link"
                                            >
                                                <TypewriterText
                                                    text={t(link.labelKey)}
                                                    active={
                                                        hoveredPanelId ===
                                                            item.id ||
                                                        openPanelId === item.id
                                                    }
                                                    className="panel__link-text"
                                                />
                                            </Link>
                                        ))}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* LANGUAGE DROPDOWN */}
                    <div
                        className={`nav__lang-drop${
                            isLangOpen ? " nav__lang-drop--open" : ""
                        }`}
                        onMouseLeave={() => setIsLangOpen(false)}
                        onBlur={(e) => {
                            if (
                                !e.currentTarget.contains(
                                    e.relatedTarget as Node
                                )
                            ) {
                                setIsLangOpen(false);
                            }
                        }}
                        onKeyDown={(e) => {
                            if (e.key === "Escape") {
                                setIsLangOpen(false);
                                const trigger =
                                    e.currentTarget.querySelector<HTMLButtonElement>(
                                        ".nav__lang-trigger"
                                    );
                                trigger?.focus();
                            }
                        }}
                    >
                        <button
                            type="button"
                            className="nav__lang-trigger"
                            onClick={() => setIsLangOpen((p) => !p)}
                            aria-haspopup="listbox"
                            aria-expanded={isLangOpen}
                            aria-label={t("nav_lang_label")}
                        >
                            <span className="nav__lang-flag" aria-hidden="true">
                                {currentLang.flag}
                            </span>
                            <span className="nav__lang-code">
                                {currentLang.code.toUpperCase()}
                            </span>
                            <svg
                                className="nav__lang-chevron"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M6 9l6 6 6-6" />
                            </svg>
                        </button>

                        <ul
                            className="nav__lang-list"
                            role="listbox"
                            aria-label={t("nav_lang_label")}
                        >
                            {languages.map((lang) => (
                                <li
                                    key={lang.code}
                                    role="option"
                                    aria-selected={language === lang.code}
                                    className={`nav__lang-option${
                                        language === lang.code
                                            ? " nav__lang-option--active"
                                            : ""
                                    }`}
                                    onClick={() => {
                                        setLanguage(lang.code);
                                        setIsLangOpen(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === "Enter" ||
                                            e.key === " "
                                        ) {
                                            e.preventDefault();
                                            setLanguage(lang.code);
                                            setIsLangOpen(false);
                                        }
                                    }}
                                    tabIndex={isLangOpen ? 0 : -1}
                                >
                                    <span aria-hidden="true">{lang.flag}</span>
                                    <span>{lang.nativeName}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* BURGER (MOBILE) */}
                <div className="nav__burger">
                    <Burger
                        onClick={handleBurgerClick}
                        aria-expanded={isBurgerOpen}
                        aria-controls="main-mobile-navigation"
                        aria-label={t("nav_burger_label")}
                    />
                </div>
            </div>

            {/* PANEL MOBILE */}
            <div
                id="main-mobile-navigation"
                className={`nav__mobilePanel ${
                    isBurgerOpen ? "nav__mobilePanel--open" : ""
                }`}
                aria-hidden={!isBurgerOpen}
                hidden={!isBurgerOpen}
            >
                {/* MOBILE LANGUAGE DROPDOWN */}
                <div
                    className={`nav__lang-drop nav__lang-drop--mobile${
                        isLangOpen ? " nav__lang-drop--open" : ""
                    }`}
                >
                    <button
                        type="button"
                        className="nav__lang-trigger"
                        onClick={() => setIsLangOpen((p) => !p)}
                        aria-haspopup="listbox"
                        aria-expanded={isLangOpen}
                        aria-label={t("nav_lang_label")}
                    >
                        <span className="nav__lang-flag" aria-hidden="true">
                            {currentLang.flag}
                        </span>
                        <span className="nav__lang-code">
                            {currentLang.code.toUpperCase()}
                        </span>
                        <svg
                            className="nav__lang-chevron"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                        >
                            <path d="M6 9l6 6 6-6" />
                        </svg>
                    </button>

                    <ul
                        className="nav__lang-list"
                        role="listbox"
                        aria-label={t("nav_lang_label")}
                    >
                        {languages.map((lang) => (
                            <li
                                key={lang.code}
                                role="option"
                                aria-selected={language === lang.code}
                                className={`nav__lang-option${
                                    language === lang.code
                                        ? " nav__lang-option--active"
                                        : ""
                                }`}
                                onClick={() => {
                                    setLanguage(lang.code);
                                    setIsLangOpen(false);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setLanguage(lang.code);
                                        setIsLangOpen(false);
                                    }
                                }}
                                tabIndex={isLangOpen ? 0 : -1}
                            >
                                <span aria-hidden="true">{lang.flag}</span>
                                <span>{lang.nativeName}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                {NAV_ITEMS.map((item) => {
                    const hasPanel = !!item.links?.length;
                    const isOpen = openMobileId === item.id;

                    return (
                        <div key={item.id} className="nav__mobileGroup">
                            <Button
                                type="button"
                                variant="primary"
                                className="nav__mobileItem"
                                aria-expanded={hasPanel ? isOpen : undefined}
                                onClick={() =>
                                    handleMobileParentClick(item, hasPanel)
                                }
                            >
                                <TypewriterText
                                    text={t(item.labelKey)}
                                    active={isBurgerOpen}
                                />
                            </Button>

                            {hasPanel && (
                                <div
                                    className={`nav__mobileSubmenu ${
                                        isOpen ? "nav__mobileSubmenu--open" : ""
                                    }`}
                                >
                                    {item.links!.map((link) => (
                                        <Link
                                            key={link.id}
                                            to={link.to}
                                            className="nav__mobileLink"
                                            onClick={handleMobileLinkClick}
                                        >
                                            <TypewriterText
                                                text={t(link.labelKey)}
                                                active={isOpen && isBurgerOpen}
                                            />
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </nav>
    );
};

export default NavComponent;
